import {
  type AccountFlowSummaries,
  type AccountFlowSummariesQuery,
  type AccountFlowSummary,
  type CashFlowQuery,
  type CashFlowReport,
  type ExpenseBreakdown,
  type ExpenseBreakdownQuery,
  reportingKeys,
} from '@taler/api-client'
import { formatLocalDate, type LocalDate } from '@taler/domain'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import type { CategorySpending } from '../internal/types.ts'

/** The window the Home screen compares: month to date, or the same window in a previous month. */
export interface HomeWindow {
  readonly from: LocalDate
  readonly until: LocalDate
}

export interface UseHomeFlowsOptions {
  readonly ledgerId: () => string | undefined
  readonly displayCurrency: () => string
  readonly window: HomeWindow
  readonly enabled: () => boolean
}

export interface HomeFlowQuery<T> {
  readonly data: ComputedRef<T | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  retry(): void
}

type HomeQuery = CashFlowQuery | ExpenseBreakdownQuery | AccountFlowSummariesQuery

/**
 * Caches the server's own response under the same key Reports uses, so the two screens share one
 * entry; `shape` derives Home's figures from that response only when it is read.
 */
function useHomeFlow<Q extends HomeQuery, R, T>(
  options: UseHomeFlowsOptions,
  build: (query: Q, ledgerId: string, signal: AbortSignal) => Promise<R>,
  keyOf: (ledgerId: string, query: Q) => readonly unknown[],
  extra: Partial<Q>,
  shape: (raw: R) => T,
): HomeFlowQuery<T> {
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)
  const query = computed(
    () =>
      ({
        from: formatLocalDate(options.window.from),
        until: formatLocalDate(options.window.until),
        displayCurrency: options.displayCurrency(),
        ...extra,
      }) as Q,
  )

  const flowQuery = useQuery({
    queryKey: computed(() => keyOf(options.ledgerId() ?? '', query.value)),
    queryFn: ({ signal }) => build(query.value, options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  return {
    data: computed(() => {
      const raw = flowQuery.data.value
      return raw === undefined ? undefined : shape(raw)
    }),
    isLoading: computed(() => active.value && flowQuery.isPending.value),
    error: computed(() => flowQuery.error.value),
    retrying: retrying(flowQuery),
    retry: () => {
      void flowQuery.refetch()
    },
  }
}

/** Spending by account; items without an account are kept, for the list to fold into its Other row. */
function categorySpendingOf(breakdown: ExpenseBreakdown): CategorySpending {
  return {
    total: breakdown.total,
    previousTotal: breakdown.previousTotal,
    items: breakdown.items.map((item) => ({
      accountId: item.group.kind === 'category' ? item.group.accountId : undefined,
      amount: item.amount,
      entryCount: item.entryCount,
      share: item.share,
    })),
  }
}

export function useCashFlow(options: UseHomeFlowsOptions): HomeFlowQuery<CashFlowReport> {
  const api = useApi()
  return useHomeFlow<CashFlowQuery, CashFlowReport, CashFlowReport>(
    options,
    (query, ledgerId, signal) => api.reporting.cashFlow(ledgerId, query, signal),
    (ledgerId, query) => reportingKeys.cashFlow(ledgerId, query),
    { bucket: 'MONTH' },
    (report) => report,
  )
}

export function useCategorySpending(options: UseHomeFlowsOptions): HomeFlowQuery<CategorySpending> {
  const api = useApi()
  return useHomeFlow<ExpenseBreakdownQuery, ExpenseBreakdown, CategorySpending>(
    options,
    (query, ledgerId, signal) => api.reporting.expenseBreakdown(ledgerId, query, signal),
    (ledgerId, query) => reportingKeys.expenseBreakdown(ledgerId, query),
    { bucket: 'MONTH', groupBy: 'CATEGORY' },
    categorySpendingOf,
  )
}

export function useAccountFlowSummaries(
  options: UseHomeFlowsOptions,
): HomeFlowQuery<readonly AccountFlowSummary[]> {
  const api = useApi()
  return useHomeFlow<AccountFlowSummariesQuery, AccountFlowSummaries, readonly AccountFlowSummary[]>(
    options,
    (query, ledgerId, signal) => api.reporting.accountFlowSummaries(ledgerId, query, signal),
    (ledgerId, query) => reportingKeys.accountFlowSummaries(ledgerId, query),
    {},
    (summaries) => summaries.accounts,
  )
}
